import Big from 'big.js';
import classNames from 'classnames';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { FormikErrors, FormikProps, FormikProvider, FormikValues, useFormik } from 'formik';
import { isNumber } from 'lodash';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import { RegisterBanksSelect } from '@/modules/common/components/RegisterBanksSelect.tsx';
import RegisterClassesSelectPaginate, {
  ClassItemOption,
} from '@/modules/common/components/RegisterClassesSelectPaginate.tsx';
import { BigRoundMode } from '@/modules/common/constants/big-round-mode.ts';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { useRegisterQuery } from '@/modules/common/queries/registers.ts';
import { queryClient } from '@/modules/common/query.ts';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import { useCurrentUserRegistersQuery } from '@/modules/investor-dashboard/queries/registers.tsx';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { ButtonDownloadABAFile } from '@/modules/issuer-dashboard/components/distribution-dashboard/DistributionMore.tsx';
import {
  DistributionActivitiesListLike,
  ShareholderEmailSelectionById,
  ShareholderEmailSetting,
  applyShareholderEmailSelectionToMany,
  collectShareholderIdsFromActivities,
  getShareholderEmailSelectionFromActivity,
  getShareholderIdFromDistributionActivity,
  hydrateMissingShareholderEmailSelections,
  mergeShareholderEmailSettings,
  updateShareholderEmailSelection,
} from '@/modules/issuer-dashboard/helpers/shareholderEmailSettings.ts';
import { useIssuerActivitiesListQuery } from '@/modules/issuer-dashboard/queries/activities.tsx';
import {
  CURRENT_ISSUER_DIVIDEND_EVENT_LIST_QUERY_KEY,
  useDividendPaymentsBulkCompleteMutation,
  useIssuerAddDividendEventMutation,
  useIssuerGeneratePreviewDividendMutation,
  useIssuerUpdateStatusDividendEventMutation,
  useTransactionAwaitingPaymentBulkCompleteMutation,
} from '@/modules/issuer-dashboard/queries/dividends.tsx';
import { useNavCreateMutation } from '@/modules/issuer-dashboard/queries/nav.tsx';
import {
  Alert,
  BaseModalProps,
  BodyCell,
  Button,
  CalendarInput,
  CalendarInputProps,
  CustomCheckbox,
  ModalContentWrapper,
  Select,
  SelectOption,
  SelectPaginateOption,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  TextInputField,
  Tooltip,
  TooltipNew,
} from 'ui';
import { PdfViewer } from 'ui/PDFViewer';
import {
  AlertIcon,
  AlertOrangeIcon,
  ArrowLeftIcon,
  InfoCircleBlueIcon,
  InfoCircleIcon,
} from 'ui/icons';
import { goodFormatNumber, goodFormatUSDCurrency } from 'ui/utils';
import {
  ActivitiesType,
  BankTypeEnum,
  CreateDividendEventRequest,
  DividendType,
  PaymentSetCompletedStatusRequest,
  PaymentStatus,
  RegisterBankExtended,
  RoundTo,
  RoundingType,
  SortedBy,
  TransactionSetAwaitPaymentStatusRequest,
  TransactionStatus,
} from 'api';

const validationSchema = yup.object<CreateDividendEventRequest & { register_id: number }>({
  name: yup
    .string()
    .trim()
    .min(2, 'Must be at least 2 characters')
    .required('This field is required'),
  amount_per_share: yup
    .number()
    .typeError('Price must be a number')
    .moreThan(0, 'Price must be greater than 0')
    .required('This field is required'),
  register_id: yup
    .number()
    .typeError('Register id must be a number')
    .required('This field is required')
    .moreThan(0, 'This field is required'),
  register_class_id: yup
    .number()
    .moreThan(0, 'This field is required')
    .required('This field is required'),
  type: yup.string().trim().required('This field is required'),
  memo: yup.string().trim(),
  date: yup.date().required('This field is required'),
  bank_id: yup.number().moreThan(0, 'This field is required').required('This field is required'),
});

const initialValues: CreateDividendEventRequest & { register_id: number } = {
  name: '',
  register_class_id: 0,
  amount_per_share: 0,
  type: DividendType.Regular,
  memo: '',
  register_id: 0,
  date: '',
  bank_id: 0,
};

const getShareholderEmailSelectionStorageKey = (registerId: number, dividendId: number) =>
  `sr-dividend-email-selection:${registerId}:${dividendId}`;

interface PersistedShareholderEmailSelection {
  payment: ShareholderEmailSelectionById;
  reinvestment: ShareholderEmailSelectionById;
}

const readPersistedShareholderEmailSelection = (
  registerId: number,
  dividendId: number,
): PersistedShareholderEmailSelection | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storageValue = window.localStorage.getItem(
      getShareholderEmailSelectionStorageKey(registerId, dividendId),
    );

    if (!storageValue) {
      return null;
    }

    const parsed = JSON.parse(storageValue) as Partial<PersistedShareholderEmailSelection>;
    const payment =
      parsed?.payment && typeof parsed.payment === 'object'
        ? parsed.payment
        : ({} as ShareholderEmailSelectionById);
    const reinvestment =
      parsed?.reinvestment && typeof parsed.reinvestment === 'object'
        ? parsed.reinvestment
        : ({} as ShareholderEmailSelectionById);

    return {
      payment,
      reinvestment,
    };
  } catch (error) {
    console.error('Failed to parse persisted shareholder email selections', error);
    return null;
  }
};

const writePersistedShareholderEmailSelection = (
  registerId: number,
  dividendId: number,
  value: PersistedShareholderEmailSelection,
) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      getShareholderEmailSelectionStorageKey(registerId, dividendId),
      JSON.stringify(value),
    );
  } catch (error) {
    console.error('Failed to persist shareholder email selections', error);
  }
};

const clearPersistedShareholderEmailSelection = (registerId: number, dividendId: number) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(getShareholderEmailSelectionStorageKey(registerId, dividendId));
  } catch (error) {
    console.error('Failed to clear persisted shareholder email selections', error);
  }
};

export interface RegisterClassSelectPaginate {
  id: number;
  price_per_share: number;
  toString: () => string;
}

interface StepItem {
  id: number;
  component: ReactNode;
  title: string;
  classNames?: string;
}

export interface IssuerAddDistributionModalProps extends BaseModalProps {
  register_id: number;
  selectedClass?: SelectPaginateOption<RegisterClassSelectPaginate>;
  modalStep?: number;
  existDividendEventId?: number;
  registerCountry: string;
  dividendDate?: string;
}

export const IssuerAddDistributionModal = ({
  open,
  onClose,
  register_id,
  selectedClass,
  modalStep = 1,
  existDividendEventId,
  registerCountry,
  dividendDate,
}: IssuerAddDistributionModalProps) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const [previewUrl, setPreviewUrl] = useState('');
  const [step, setStep] = useState(modalStep);
  const [totalStep, setTotalStep] = useState(4);
  const [dividendId, setDividendId] = useState(existDividendEventId || 0);
  const [dividendIssueDate, setDividendIssueDate] = useState(dividendDate || '');
  const [paymentEmailSelectionByShareholderId, setPaymentEmailSelectionByShareholderId] =
    useState<ShareholderEmailSelectionById>({});
  const [reinvestmentEmailSelectionByShareholderId, setReinvestmentEmailSelectionByShareholderId] =
    useState<ShareholderEmailSelectionById>({});
  const hydratedSelectionStorageKeyRef = useRef('');

  const getInitialValues = (): CreateDividendEventRequest & { register_id: number } => {
    return {
      ...initialValues,
      ...(selectedClass
        ? {
            register_class_id: selectedClass.value.id,
            amount_per_share: 0, //selectedClass.value.price_per_share,
          }
        : {}),
      date: new Date().toISOString(),
      register_id,
    };
  };

  const formik = useFormik<CreateDividendEventRequest & { register_id: number }>({
    initialValues: getInitialValues(),
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async values => {
      await generatePreviewDividend(values);
    },
    enableReinitialize: false,
  });

  const { mutateAsync: generatePreviewDividend, isLoading: isPreviewDividendGenerating } =
    useIssuerGeneratePreviewDividendMutation({
      setErrors: formik.setErrors,
      onSuccess: previewUrl => {
        setPreviewUrl(previewUrl ?? '');
        setStep(2);
      },
    });

  const { data: registerShareholdersData } = useRegisterShareholderList(
    { register_id: formik.values.register_id },
    { disabled: !formik.values.register_id },
  );

  const shareholderIdByEntityId = useMemo(() => {
    return (registerShareholdersData?.shareholders || []).reduce<Record<number, number>>(
      (acc, item) => {
        if (item.entity_id && item.id) {
          acc[item.entity_id] = item.id;
        }
        return acc;
      },
      {},
    );
  }, [registerShareholdersData?.shareholders]);

  const shareholderEmailSettings = useMemo<ShareholderEmailSetting[]>(
    () =>
      mergeShareholderEmailSettings(
        paymentEmailSelectionByShareholderId,
        reinvestmentEmailSelectionByShareholderId,
      ),
    [paymentEmailSelectionByShareholderId, reinvestmentEmailSelectionByShareholderId],
  );

  useEffect(() => {
    if (!open || register_id <= 0 || dividendId <= 0) {
      return;
    }

    const storageKey = getShareholderEmailSelectionStorageKey(register_id, dividendId);
    if (hydratedSelectionStorageKeyRef.current === storageKey) {
      return;
    }

    const persistedSelection = readPersistedShareholderEmailSelection(register_id, dividendId);
    if (persistedSelection) {
      setPaymentEmailSelectionByShareholderId(persistedSelection.payment);
      setReinvestmentEmailSelectionByShareholderId(persistedSelection.reinvestment);
    }

    hydratedSelectionStorageKeyRef.current = storageKey;
  }, [dividendId, open, register_id]);

  useEffect(() => {
    if (register_id <= 0 || dividendId <= 0) {
      return;
    }

    writePersistedShareholderEmailSelection(register_id, dividendId, {
      payment: paymentEmailSelectionByShareholderId,
      reinvestment: reinvestmentEmailSelectionByShareholderId,
    });
  }, [
    dividendId,
    paymentEmailSelectionByShareholderId,
    register_id,
    reinvestmentEmailSelectionByShareholderId,
  ]);

  const clearPersistedSelectionOnFinalize = useCallback(() => {
    if (register_id <= 0 || dividendId <= 0) {
      return;
    }

    clearPersistedShareholderEmailSelection(register_id, dividendId);
  }, [dividendId, register_id]);

  const currentStep = useMemo<StepItem | undefined>(() => {
    const steps: StepItem[] = [
      {
        id: 1,
        component: (
          <NewDistribution
            formik={formik}
            isPreviewDividendGenerating={isPreviewDividendGenerating}
            selectedClass={selectedClass}
          />
        ),
        title: `New ${getRegisterTypeStrings('dividend')}`,
      },
      {
        id: 2,
        component: (
          <PreviewPDFReport
            setStep={setStep}
            previewUrl={previewUrl}
            value={formik.values}
            setErrors={formik.setErrors}
            setDividendId={setDividendId}
            setDividendIssueDate={setDividendIssueDate}
          />
        ),
        title: `Confirm ${getRegisterTypeStrings('dividend')} creation`,
      },
      {
        id: 3,
        component: (
          <ConfirmPayments
            setStep={setStep}
            registerId={formik.values.register_id}
            dividendId={dividendId}
            dividendDate={dividendIssueDate}
            onClose={onClose}
            registerCountry={registerCountry}
            setTotalStep={setTotalStep}
            shareholderIdByEntityId={shareholderIdByEntityId}
            emailSelectionByShareholderId={paymentEmailSelectionByShareholderId}
            setEmailSelectionByShareholderId={setPaymentEmailSelectionByShareholderId}
            shareholderEmailSettings={shareholderEmailSettings}
            onFinalize={clearPersistedSelectionOnFinalize}
          />
        ),
        title: 'Confirm payments',
        classNames: 'p-0 pt-3 sm:pt-6',
      },
      {
        id: 4,
        component: (
          <ConfirmReinvestments
            registerId={formik.values.register_id}
            dividendId={dividendId}
            onClose={onClose}
            dividendIssueDate={dividendIssueDate}
            registerClassId={formik.values.register_class_id}
            shareholderIdByEntityId={shareholderIdByEntityId}
            emailSelectionByShareholderId={reinvestmentEmailSelectionByShareholderId}
            setEmailSelectionByShareholderId={setReinvestmentEmailSelectionByShareholderId}
            shareholderEmailSettings={shareholderEmailSettings}
            onFinalize={clearPersistedSelectionOnFinalize}
          />
        ),
        classNames: 'p-0 pt-3 sm:pt-6',
        title: 'Confirm reinvestments',
      },
    ];

    return steps.find(item => item.id === step);
  }, [
    dividendId,
    dividendIssueDate,
    formik.values,
    getRegisterTypeStrings,
    isPreviewDividendGenerating,
    clearPersistedSelectionOnFinalize,
    onClose,
    paymentEmailSelectionByShareholderId,
    previewUrl,
    reinvestmentEmailSelectionByShareholderId,
    registerCountry,
    shareholderEmailSettings,
    shareholderIdByEntityId,
    selectedClass,
    step,
  ]);

  const handleCloseWithPrompt = async () => {
    const confirmed =
      step === 1 && !formik.dirty
        ? true
        : confirm('Do you really want to leave this form? You will lose some entered information.');
    if (confirmed) {
      await queryClient.invalidateQueries([CURRENT_ISSUER_DIVIDEND_EVENT_LIST_QUERY_KEY]);
      onClose();
    }
  };

  return (
    <ModalContentWrapper
      open={open}
      title={currentStep?.title}
      onClose={handleCloseWithPrompt}
      classes={{ children: currentStep?.classNames, dialogPanel: 'sm:min-w-[880px]' }}
    >
      <div className="flex flex-col min-h-[300px] justify-between w-full gap-8">
        <div
          className={classNames({
            'px-3 sm:px-6': !!currentStep?.classNames?.includes('p-'),
          })}
        >
          <StepIndicator step={step} totalSteps={totalStep} />
        </div>
        <div className="w-full">{currentStep?.component}</div>
      </div>
    </ModalContentWrapper>
  );
};

interface StepIndicatorProps {
  step: number;
  totalSteps: number;
}

const StepIndicator = ({ step, totalSteps }: StepIndicatorProps) => {
  const progress = (step / totalSteps) * 100;

  return (
    <div className="flex items-center gap-2">
      <div className="relative w-5 h-5">
        <svg className="w-full h-full" viewBox="0 0 36 36">
          <circle cx="18" cy="18" r="16" fill="none" stroke="#E5E7EB" strokeWidth="4" />
          <circle
            cx="18"
            cy="18"
            r="16"
            fill="none"
            stroke="#000"
            strokeWidth="4"
            strokeDasharray="100, 100"
            strokeDashoffset={100 - progress}
            transform="rotate(-90 18 18)"
          />
        </svg>
      </div>
      <span className="text-[#171717] text-sm">
        Step {step} of {totalSteps}
      </span>
    </div>
  );
};

interface ShareholderEmailControlsProps {
  isAllSelected: boolean;
  isIndeterminate: boolean;
  onToggleAll: (checked: boolean) => void;
  className?: string;
}

const ShareholderEmailMasterControl = ({
  isAllSelected,
  isIndeterminate,
  onToggleAll,
  className,
}: ShareholderEmailControlsProps) => {
  return (
    <div className={classNames('flex items-center gap-2 whitespace-nowrap shrink-0', className)}>
      <span className="text-sm leading-5 text-gray-900">Notify all investors by email</span>
      <CustomCheckbox
        className="select-none mr-0 leading-none"
        checked={isAllSelected}
        indeterminate={isIndeterminate}
        onChange={event => onToggleAll(event.target.checked)}
        aria-label="Notify all investors by email"
      />
    </div>
  );
};

interface ShareholderEmailRowCheckboxProps {
  investorName: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const ShareholderEmailRowCheckbox = ({
  investorName,
  checked,
  onChange,
}: ShareholderEmailRowCheckboxProps) => {
  return (
    <div
      className="flex justify-center"
      onClick={event => {
        event.stopPropagation();
      }}
    >
      <TooltipNew
        text={checked ? 'Do not send email to this holder' : 'Notify this holder by email'}
        place="left"
        offset={10}
      >
        <CustomCheckbox
          checked={checked}
          onChange={event => onChange(event.target.checked)}
          aria-label={`Notify ${investorName} by email`}
        />
      </TooltipNew>
    </div>
  );
};

interface UseShareholderEmailSelectionParams {
  issuerActivitiesList?: DistributionActivitiesListLike;
  shareholderIdByEntityId: Record<number, number>;
  emailSelectionByShareholderId: ShareholderEmailSelectionById;
  setEmailSelectionByShareholderId: Dispatch<SetStateAction<ShareholderEmailSelectionById>>;
}

const useShareholderEmailSelection = ({
  issuerActivitiesList,
  shareholderIdByEntityId,
  emailSelectionByShareholderId,
  setEmailSelectionByShareholderId,
}: UseShareholderEmailSelectionParams) => {
  const shareholderIds = useMemo<number[]>(() => {
    return collectShareholderIdsFromActivities(issuerActivitiesList, shareholderIdByEntityId);
  }, [issuerActivitiesList, shareholderIdByEntityId]);

  useEffect(() => {
    if (!shareholderIds.length) {
      return;
    }

    setEmailSelectionByShareholderId(prev =>
      hydrateMissingShareholderEmailSelections(prev, shareholderIds),
    );
  }, [setEmailSelectionByShareholderId, shareholderIds]);

  const isAllShareholdersSelected =
    shareholderIds.length > 0 &&
    shareholderIds.every(shareholderId => emailSelectionByShareholderId[shareholderId] ?? true);

  const isSomeShareholdersSelected = shareholderIds.some(
    shareholderId => emailSelectionByShareholderId[shareholderId] ?? true,
  );

  const isMasterCheckboxIndeterminate = isSomeShareholdersSelected && !isAllShareholdersSelected;

  const handleToggleAllShareholders = useCallback(
    (checked: boolean) => {
      setEmailSelectionByShareholderId(prev =>
        applyShareholderEmailSelectionToMany(prev, shareholderIds, checked),
      );
    },
    [setEmailSelectionByShareholderId, shareholderIds],
  );

  return {
    isAllShareholdersSelected,
    isMasterCheckboxIndeterminate,
    handleToggleAllShareholders,
  };
};

interface DistributionEmailPrivacyWarningParams {
  issuerActivitiesList?: DistributionActivitiesListLike;
  shareholderIdByEntityId: Record<number, number>;
  emailSelectionByShareholderId: ShareholderEmailSelectionById;
}

const getDistributionEmailPrivacyWarning = ({
  issuerActivitiesList,
  shareholderIdByEntityId,
  emailSelectionByShareholderId,
}: DistributionEmailPrivacyWarningParams): string | null => {
  if (!issuerActivitiesList) {
    return null;
  }

  let hasSelectedRecipient = false;
  const seen = new Set<number>();

  issuerActivitiesList.pages.forEach(page => {
    page.data?.forEach(activity => {
      const shareholderId = getShareholderIdFromDistributionActivity(
        activity,
        shareholderIdByEntityId,
      );
      if (!shareholderId || seen.has(shareholderId)) {
        return;
      }

      const isSelected = emailSelectionByShareholderId[shareholderId] ?? true;
      if (!isSelected) {
        return;
      }

      hasSelectedRecipient = true;
      seen.add(shareholderId);
    });
  });

  if (!hasSelectedRecipient) {
    return null;
  }

  return (
    'We will automatically send investor an email containing an Investor Distribution report' +
    ' attached and inform them about their distribution. If you chose not to send the email,' +
    ' we will still automatically generated Investor Distribution report per each holder' +
    ' who received a distribution and you will be able to access it from Reports button' +
    ' on Distribution page under your distribution (reports may appear in a few minutes after' +
    ' creating the distribution).'
  );
};

interface NewDistributionProps {
  selectedClass?: SelectPaginateOption<RegisterClassSelectPaginate>;
  formik: FormikProps<CreateDividendEventRequest & { register_id: number }>;
  isPreviewDividendGenerating: boolean;
}

const NewDistribution = ({
  selectedClass,
  formik,
  isPreviewDividendGenerating,
}: NewDistributionProps) => {
  const [params, setParams] = useSearchParams();
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const { data: registersResponse, isLoading: isRegistersLoading } = useCurrentUserRegistersQuery();

  const registerOptions = useMemo<SelectOption<number>[]>(() => {
    if (!registersResponse?.data) return [];

    return registersResponse?.data.map(({ register_id, register_obj }) => ({
      label: register_obj?.name || '',
      value: register_id,
    }));
  }, [registersResponse?.data]);

  const handleChangeRegister = (registerId: number | null) => {
    if (!registerId) return;
    formik.setFieldValue('register_id', registerId);
    formik.setFieldError('register_id', '');
    params.set(REGISTER_ID_SEARCH_PARAM_KEY, String(registerId));
    setParams(params);
    const registerName = registerOptions.find(({ value }) => value === registerId)?.label;
    toast.success(`We have changed your current register to ${registerName}`, {
      duration: 4000,
    });
  };

  const handleChangeBank = (item: SelectPaginateOption<RegisterBankExtended> | null) => {
    formik.setFieldValue('bank_id', item?.value.id);
    formik.setFieldError('bank_id', '');
  };

  // const handleChangeDistributionType = (type: DividendType | null) => {
  //   if (!type) return;
  //   formik.setFieldValue('type', type);
  //   formik.setFieldError('type', '');
  // };

  const handleChangePrice = (value: string | number) => {
    formik.setFieldValue('amount_per_share', value);
    formik.setFieldError('amount_per_share', '');
  };

  const handleChangeDate: CalendarInputProps['onChange'] = date => {
    if (!date) {
      formik.setFieldError('date', '');
      formik.setFieldValue('date', '');
      return;
    }
    // const currentDate = isSameDay(date?.toDate(), new Date())
    //   ? new Date().toISOString()
    //   : date?.toDate().toISOString();
    const isoStringWithOffset = format(
      new Date(date?.toDate().toISOString()),
      "yyyy-MM-dd'T'HH:mm:ssXXX",
    );
    formik.setFieldError('date', '');
    formik.setFieldValue('date', isoStringWithOffset);
  };

  const handleChangeRegisterClass = (options: SelectPaginateOption<ClassItemOption> | null) => {
    const register_class_id = options?.value?.id || null;

    if (options?.value?.current_price_per_share === 0) {
      toast(
        () => (
          <span className="flex flex-col justify-center items-center gap-2 text-sm">
            <span>
              <img src={AlertOrangeIcon} className="w-8 h-8" alt="alert" />
            </span>
            <div>You can't distribute to a class with zero {getRegisterTypeStrings('nav')}.</div>
          </span>
        ),
        {
          duration: 5000,
          id: 'reset',
        },
      );
      return;
    }

    formik.setFieldValue('register_class_id', register_class_id);
    // formik.setFieldValue('amount_per_share', price_per_share);
    formik.setFieldError('register_class_id', '');
  };

  return (
    <FormikProvider value={formik}>
      <form className="w-full flex flex-col space-y-10" onSubmit={preventFormDefault}>
        <div className="w-full flex flex-col space-y-8">
          <TextInputField name="name" label="Label" />
          <Select
            label="REGISTER"
            disabled={!!selectedClass}
            options={registerOptions}
            value={formik.values.register_id}
            isLoading={isRegistersLoading}
            onChange={handleChangeRegister}
            error={formik.errors.register_id}
            isClearable={true}
          />
          <RegisterClassesSelectPaginate
            // key={'Class' + formik.values.register_id}
            label="CLASS"
            disabled={!!selectedClass}
            onChange={handleChangeRegisterClass}
            value={formik.values.register_class_id || selectedClass?.value?.id}
            error={formik.errors.register_class_id}
            registerId={formik.values.register_id}
            clearable={true}
            isMulti={false}
          />
          {!!formik.values.register_class_id && !!formik.values.register_id && (
            <RegisterBanksSelect
              onChange={handleChangeBank}
              value={formik.values.bank_id}
              label="Bank"
              clearable={true}
              error={formik.errors.bank_id}
              registerId={formik.values.register_id}
              needBankType={BankTypeEnum.Dividend}
              needClassId={formik.values.register_class_id}
              key={`register_${formik.values.register_id}_class_${formik.values.register_class_id}`}
              is_overall={true}
            />
          )}
          {/*<Select*/}
          {/*  name="type"*/}
          {/*  options={typeOptions}*/}
          {/*  value={formik.values.type}*/}
          {/*  label={`${getRegisterTypeStrings('dividend')} type`}*/}
          {/*  onChange={handleChangeDistributionType}*/}
          {/*  error={formik.errors.type}*/}
          {/*  isClearable={true}*/}
          {/*/>*/}
          <CurrencyInput
            name="amount_per_share"
            label="Distribution amount per share/unit"
            value={formik.values.amount_per_share ?? ''}
            error={formik.errors.amount_per_share}
            onChange={handleChangePrice}
            decimalPoints={6}
          />
          <CalendarInput
            showTime
            label={`${getRegisterTypeStrings('dividend')} date`}
            name="date"
            value={formik.values.date ? dayjs(formik.values.date) : null}
            onChange={handleChangeDate}
            format="DD-MM-YYYY HH:mm"
            error={formik.errors.date}
          />
          <TextInputField textarea name="memo" label="Memo (Optional)" />
          <Alert
            variant="neutral-2"
            icon={InfoCircleBlueIcon}
            label={
              'This operation will create a payment or DRP transaction per each shareholder of this class. ' +
              'You need to confirm these transactions on Operations page to complete them.'
            }
          />
        </div>
        <Button
          loading={isRegistersLoading || isPreviewDividendGenerating}
          onClick={formik.handleSubmit}
          type="submit"
        >
          Preview {getRegisterTypeStrings('dividend')} summary
        </Button>
      </form>
    </FormikProvider>
  );
};

export interface PreviewPDFReportProps {
  value?: CreateDividendEventRequest & { register_id: number };
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  previewUrl: string;
  setStep: Dispatch<SetStateAction<number>>;
  setDividendId: Dispatch<SetStateAction<number>>;
  setDividendIssueDate: Dispatch<SetStateAction<string>>;
}

export const PreviewPDFReport = ({
  value,
  setErrors,
  previewUrl,
  setStep,
  setDividendId,
  setDividendIssueDate,
}: PreviewPDFReportProps) => {
  const { mutateAsync: addDividendEvent, isLoading: isDividendCreation } =
    useIssuerAddDividendEventMutation({
      setErrors,
      onSuccess: data => {
        setDividendId(data.divided_event.id);
        setDividendIssueDate(format(new Date(data.divided_event.date), "yyyy-MM-dd'T'HH:mm:ssXXX"));
        setStep(3);
      },
    });

  const onBack = () => {
    setStep(1);
  };

  return (
    <div className="w-full flex flex-col gap-8">
      <div className="w-full flex flex-col gap-2">
        <button
          onClick={onBack}
          className={classNames('flex-none', {
            'hover:opacity-80 hover:cursor-pointer': !(isDividendCreation || !value || !previewUrl), //not disabled
            'opacity-40': isDividendCreation || !value || !previewUrl, //disabled
          })}
          disabled={isDividendCreation || !value || !previewUrl}
        >
          <img src={ArrowLeftIcon} alt="back" className="w-6 h-6 min-w-6 min-h-6" />
        </button>

        <div className="w-full flex flex-col items-center justify-center min-h-[200px] gap-4">
          {!previewUrl ? (
            <Spinner size="lg" />
          ) : (
            <>
              <PdfViewer
                src={previewUrl}
                removeFirstPageTopPadding
                className="w-full sm:max-w-[800px] max-w-[500px]"
              />
              {/* <iframe */}
              {/*   src={previewUrl} */}
              {/*   className="w-full max-w-[500px] aspect-[0.8]" */}
              {/*   allow="fullscreen" */}
              {/*   allowFullScreen={true} */}
              {/* ></iframe> */}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col w-full gap-4">
        <a href={previewUrl} target="_blank" rel="noopener noreferrer">
          <Button
            // loading={isDividendCreation}
            disabled={isDividendCreation || !value || !previewUrl}
            variant="plain"
          >
            Open in a new tab
          </Button>
        </a>

        {/* <Button */}
        {/*   loading={isDividendCreation} */}
        {/*   onClick={() => { */}
        {/*     setStep(1); */}
        {/*   }} */}
        {/*   disabled={isDividendCreation || !value || !previewUrl} */}
        {/*   variant="secondary" */}
        {/* > */}
        {/*   Back */}
        {/* </Button> */}

        <Button
          loading={isDividendCreation}
          onClick={() => {
            value && addDividendEvent(value);
          }}
          disabled={isDividendCreation || !value || !previewUrl}
          type="submit"
        >
          Confirm
        </Button>
      </div>
    </div>
  );
};

type TypeHeadConfirmPayments = 'Investor' | 'Status' | 'Total' | 'Notify';

interface PaymentsRequest extends PaymentSetCompletedStatusRequest {
  amount?: any;
  status?: PaymentStatus;
}

interface ConfirmPaymentsProps {
  setStep: Dispatch<SetStateAction<number>>;
  registerId: number;
  dividendId: number;
  onClose: () => void;
  dividendDate: string;
  registerCountry: string;
  setTotalStep: Dispatch<SetStateAction<number>>;
  shareholderIdByEntityId: Record<number, number>;
  emailSelectionByShareholderId: ShareholderEmailSelectionById;
  setEmailSelectionByShareholderId: Dispatch<SetStateAction<ShareholderEmailSelectionById>>;
  shareholderEmailSettings: ShareholderEmailSetting[];
  onFinalize: () => void;
}

const ConfirmPayments = ({
  registerId,
  dividendId,
  dividendDate,
  setStep,
  onClose,
  registerCountry,
  setTotalStep,
  shareholderIdByEntityId,
  emailSelectionByShareholderId,
  setEmailSelectionByShareholderId,
  shareholderEmailSettings,
  onFinalize,
}: ConfirmPaymentsProps) => {
  const {
    data: issuerActivitiesList,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingActivitiesList,
    isError,
  } = useIssuerActivitiesListQuery(
    {
      page: 1,
      page_size: 30,
      sorted_by: SortedBy.Status,
      dividend_id: dividendId,
      type: ActivitiesType.FundToInvestor,
    },
    false,
    Number(registerId),
  );
  const [isCompleted, setIsCompleted] = useState(true);
  const [payments, setPayments] = useState<PaymentsRequest[]>([]);

  const { data: registerData } = useRegisterQuery(registerId, !registerId);

  const { rounding_type } = useMemo(() => {
    const { rounding_type } = registerData?.data ?? {};
    return { rounding_type };
  }, [registerData]);

  const { mutateAsync: updateDividendFinalize, isLoading: isLoadingFinalize } =
    useIssuerUpdateStatusDividendEventMutation({
      onSuccess: async () => {
        onFinalize();
        await queryClient.invalidateQueries([CURRENT_ISSUER_DIVIDEND_EVENT_LIST_QUERY_KEY]);
        onClose();
      },
    });

  const { data: drpList, isLoading: isLoadingDrpList } = useIssuerActivitiesListQuery(
    {
      page: 1,
      page_size: 30,
      sorted_by: SortedBy.Status,
      dividend_id: dividendId,
      type: ActivitiesType.Drp,
    },
    false,
    Number(registerId),
  );

  const drpHasData = useMemo(
    () => (drpList && drpList.pages.some(page => page.data && page.data.length > 0)) ?? false,
    [drpList],
  );

  useEffect(() => {
    setTotalStep(drpHasData ? 4 : 3);
  }, [drpHasData, setTotalStep]);

  const handleOnSuccess = useCallback(() => {
    if (!isLoadingDrpList) {
      const drpHasData = drpList && drpList.pages.some(page => page.data && page.data.length > 0);

      if (drpHasData) {
        setStep(4);
      } else {
        setTotalStep(3);
        updateDividendFinalize({
          register_id: registerId,
          id: dividendId,
          shareholder_email_settings: shareholderEmailSettings,
        });
      }
    }
  }, [
    dividendId,
    drpList,
    isLoadingDrpList,
    registerId,
    shareholderEmailSettings,
    setStep,
    setTotalStep,
    updateDividendFinalize,
  ]);

  const { mutateAsync, isLoading } = useDividendPaymentsBulkCompleteMutation({
    onSuccess: handleOnSuccess,
  });

  useEffect(() => {
    if (!isLoadingActivitiesList) {
      if (
        issuerActivitiesList &&
        issuerActivitiesList.pages &&
        issuerActivitiesList.pages.some(page => page.data && page.data.length > 0)
      ) {
        const currentPayments: PaymentsRequest[] = [];
        const currentConfirmationDate = format(new Date(), "yyyy-MM-dd'T'HH:mm:ssXXX");

        issuerActivitiesList.pages.forEach(page => {
          page.data?.forEach(data => {
            currentPayments.push({
              id: data.id,
              amount: data.amount,
              confirmation_date: currentConfirmationDate,
              memo: '',
              round_to: rounding_type as unknown as RoundTo,
              status: data?.status as PaymentStatus,
            });
          });
        });
        setPayments(currentPayments);
      } else {
        handleOnSuccess();
      }
    }
  }, [
    issuerActivitiesList,
    isLoadingActivitiesList,
    rounding_type,
    setStep,
    drpHasData,
    handleOnSuccess,
  ]);

  const total = useMemo(() => {
    const total = payments.reduce(
      (sum, value) => sum.plus(new Big(value.amount ? +value.amount : 0)),
      new Big(0),
    );

    return total.toString();
  }, [payments]);

  const { isAllShareholdersSelected, isMasterCheckboxIndeterminate, handleToggleAllShareholders } =
    useShareholderEmailSelection({
      issuerActivitiesList,
      shareholderIdByEntityId,
      emailSelectionByShareholderId,
      setEmailSelectionByShareholderId,
    });

  const headItems = useMemo<TableHeadColumn<TypeHeadConfirmPayments>[]>(() => {
    return [
      { title: 'Investor', className: 'min-w-[250px]' },
      { title: 'Status', className: 'w-[200px]' },
      { title: 'Total', className: 'w-[200px]' },
      {
        title: 'Notify',
        className: 'w-[72px] min-w-[72px] px-0 pl-0 pr-0 text-center whitespace-nowrap',
      },
    ];
  }, []);

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!issuerActivitiesList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    issuerActivitiesList.pages.forEach(page => {
      result = result.concat(
        page.data?.map(data => {
          const payment = payments.find(item => item.id === data.id);
          const amount = payment?.amount;
          const status = payment?.status;
          const investorName = data.from_entity_name ?? data.from_user_name ?? '-';
          const shareholderId = getShareholderIdFromDistributionActivity(
            data,
            shareholderIdByEntityId,
          );
          const isShareholderSelected = getShareholderEmailSelectionFromActivity(
            data,
            shareholderIdByEntityId,
            emailSelectionByShareholderId,
          );

          const body: BodyCell[] = [
            {
              cell: (
                <div className="flex flex-col">
                  <div className="text-sm whitespace-nowrap max-w-[200px] overflow-hidden text-ellipsis">
                    {investorName}
                  </div>
                </div>
              ),
            },
            {
              cell: (
                <div>
                  <ActivityStatusBadge status={status || ''} />
                </div>
              ),
            },
            {
              cell: (
                <div>
                  <CurrencyInput
                    disabled={status !== 'pending'}
                    className="min-w-0 max-w-[110px]"
                    value={amount ?? ''}
                    onChange={value => {
                      if (!isNaN(+value) && isNumber(+value)) {
                        const currentConfirmationDate = format(
                          new Date(),
                          "yyyy-MM-dd'T'HH:mm:ssXXX",
                        );
                        setPayments(prev =>
                          prev.map(item => {
                            if (item.id !== data.id) {
                              return item;
                            }
                            return {
                              ...item,
                              confirmation_date: currentConfirmationDate,
                              amount: value,
                            };
                          }),
                        );
                      }
                    }}
                  />
                </div>
              ),
            },
            {
              className: 'w-[40px] min-w-[40px] px-0 pl-0 pr-0 text-center',
              cell: (
                <ShareholderEmailRowCheckbox
                  investorName={investorName}
                  checked={isShareholderSelected}
                  onChange={checked => {
                    if (!shareholderId) {
                      return;
                    }

                    setEmailSelectionByShareholderId(prev =>
                      updateShareholderEmailSelection(prev, shareholderId, checked),
                    );
                  }}
                />
              ),
            },
          ];

          return {
            cells: body,
          };
        }),
      );
    });

    if (total) {
      result = result.concat([
        {
          cells: [
            {
              cell: (
                <div className="flex flex-col py-2">
                  <span className="text-sm font-bold">Total</span>
                </div>
              ),
            },
            {
              cell: <div className="font-bold"></div>,
            },
            {
              cell: <div className="font-bold">{goodFormatUSDCurrency(+total)}</div>,
            },
            {
              cell: <div></div>,
            },
          ],
        },
      ]);
    }

    return result;
  }, [
    emailSelectionByShareholderId,
    issuerActivitiesList,
    payments,
    setEmailSelectionByShareholderId,
    shareholderIdByEntityId,
    total,
  ]);

  const emailPrivacyWarning = useMemo(
    () =>
      getDistributionEmailPrivacyWarning({
        issuerActivitiesList,
        shareholderIdByEntityId,
        emailSelectionByShareholderId,
      }),
    [emailSelectionByShareholderId, issuerActivitiesList, shareholderIdByEntityId],
  );

  const handleCompletePayments = useCallback(() => {
    mutateAsync({
      payments: payments
        .filter(item => item.status === PaymentStatus.Pending)
        .map<PaymentSetCompletedStatusRequest>(item => ({
          ...item,
          amount: +item.amount,
          is_need_complete: isCompleted,
        })),
      dividend_event_id: dividendId,
    });
  }, [isCompleted, mutateAsync, payments, dividendId]);

  return (
    <div className="w-full flex flex-col gap-8">
      {isLoadingActivitiesList && (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      <div className="w-full flex flex-col gap-4">
        <div className="px-3 sm:px-6 flex items-center justify-between gap-4">
          <CustomCheckbox
            label={
              <div className="flex items-center gap-2">
                <span>I've completed all payments</span>{' '}
                <Tooltip
                  delayTimeOpen={0}
                  delayTimeClose={0}
                  button={
                    <img
                      alt="info"
                      src={InfoCircleIcon}
                      className="w-[18px] h-[18px] min-h-[18px] min-w-[18px]"
                    />
                  }
                  variant="gray"
                  classNameTooltip="border border-gray-400"
                >
                  {"Check this checkbox only if you've already sent all the payments. " +
                    'This will automatically move them all to completed status in Compli One. ' +
                    "If you don't check it, they will stay in Pending status and you can mark them as completed one by one from Payments page"}
                </Tooltip>
              </div>
            }
            checked={isCompleted}
            onChange={e => setIsCompleted(e.target.checked)}
            className="select-none"
          />
          {!isLoadingActivitiesList && !!bodyItems.length && (
            <ShareholderEmailMasterControl
              isAllSelected={isAllShareholdersSelected}
              isIndeterminate={isMasterCheckboxIndeterminate}
              onToggleAll={handleToggleAllShareholders}
              className="pr-6"
            />
          )}
        </div>
        {!isLoadingActivitiesList && !!bodyItems.length && (
          <Table
            className="my-0"
            withRounded={false}
            withBorderX={false}
            headItems={headItems}
            items={bodyItems}
            isLoading={isLoadingActivitiesList}
            isError={isError}
            hasNextPageInfinite={hasNextPage}
            fetchNextPageInfinite={fetchNextPage}
            blockIdInfinite="scrollable"
          />
        )}
      </div>
      <div className="flex flex-col w-full gap-4 px-3 sm:px-6">
        {emailPrivacyWarning && (
          <Alert icon={AlertIcon} variant="error" className="mb-0">
            <div className="whitespace-normal">{emailPrivacyWarning}</div>
          </Alert>
        )}
        <Button
          loading={isLoading || isLoadingActivitiesList || isLoadingFinalize}
          onClick={handleCompletePayments}
          disabled={isLoading || isLoadingActivitiesList || isLoadingFinalize}
          type="submit"
        >
          Confirm all payments
        </Button>
        <ButtonDownloadABAFile
          dividendId={dividendId}
          date={dividendDate ? new Date(dividendDate).toISOString() : new Date().toISOString()}
          registerCountry={registerCountry}
          className="w-full"
          variant="secondary"
        />
        {/* <span className="text-xs leading-5 text-[#525252] text-center"> */}
        {/*   By submitting this form, you move all payments to Completed status */}
        {/* </span> */}
      </div>
    </div>
  );
};

type TypeHeadConfirmReinvestments =
  | 'Investor'
  | 'Distributed'
  | 'After rounding'
  | 'Unit price'
  | 'Units'
  | 'Notify';

interface TransactionRequest extends TransactionSetAwaitPaymentStatusRequest {
  total?: number;
  changed: boolean;
  price_per_share: any;
  status?: string;
  error?: string;
}

interface ConfirmReinvestmentsProps {
  registerId: number;
  registerClassId: number;
  dividendId: number;
  onClose: () => void;
  dividendIssueDate: string;
  shareholderIdByEntityId: Record<number, number>;
  emailSelectionByShareholderId: ShareholderEmailSelectionById;
  setEmailSelectionByShareholderId: Dispatch<SetStateAction<ShareholderEmailSelectionById>>;
  shareholderEmailSettings: ShareholderEmailSetting[];
  onFinalize: () => void;
}

const ConfirmReinvestments = ({
  registerId,
  dividendId,
  onClose,
  dividendIssueDate,
  registerClassId,
  shareholderIdByEntityId,
  emailSelectionByShareholderId,
  setEmailSelectionByShareholderId,
  shareholderEmailSettings,
  onFinalize,
}: ConfirmReinvestmentsProps) => {
  const {
    data: issuerActivitiesList,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingActivitiesList,
    isError,
  } = useIssuerActivitiesListQuery(
    {
      page: 1,
      page_size: 30,
      sorted_by: SortedBy.Status,
      dividend_id: dividendId,
      type: ActivitiesType.Drp,
    },
    false,
    Number(registerId),
  );
  const [transactions, setTransactions] = useState<TransactionRequest[]>([]);
  const [defaultPricePerShare, setDefaultPricePerShare] = useState<number | string>(0);
  const [errorDefaultPricePerShare, setErrorDefaultPricePerShare] = useState('');
  const [isSetCurrentNAV, setIsSetCurrentNAV] = useState(false);

  const { data: registerData } = useRegisterQuery(registerId, !registerId);

  const { rounding_type, allows_fractional_shares } = useMemo(() => {
    const { rounding_type, allows_fractional_shares } = registerData?.data ?? {};
    return { rounding_type, allows_fractional_shares };
  }, [registerData]);

  const { mutateAsync: addNav, isLoading: isLoadingAddNav } = useNavCreateMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries([CURRENT_ISSUER_DIVIDEND_EVENT_LIST_QUERY_KEY]);
      onClose();
    },
  });

  const handleOnClose = useCallback(async () => {
    if (
      isSetCurrentNAV &&
      defaultPricePerShare &&
      isNumber(+defaultPricePerShare) &&
      dividendIssueDate
    ) {
      await addNav({
        register_class_id: registerClassId,
        price_per_share: +defaultPricePerShare,
        changes_date: dayjs(dividendIssueDate).utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]'),
      });
      return;
    }
    await queryClient.invalidateQueries([CURRENT_ISSUER_DIVIDEND_EVENT_LIST_QUERY_KEY]);
    onClose();
  }, [addNav, defaultPricePerShare, dividendIssueDate, isSetCurrentNAV, onClose, registerClassId]);

  const { mutateAsync: updateDividendFinalize, isLoading: isLoadingFinalize } =
    useIssuerUpdateStatusDividendEventMutation({
      onSuccess: async () => {
        onFinalize();
        await handleOnClose();
      },
    });

  const handleOnSuccess = useCallback(async () => {
    await updateDividendFinalize({
      register_id: registerId,
      id: dividendId,
      shareholder_email_settings: shareholderEmailSettings,
    });
  }, [dividendId, registerId, shareholderEmailSettings, updateDividendFinalize]);

  const { mutateAsync, isLoading } = useTransactionAwaitingPaymentBulkCompleteMutation({
    onSuccess: handleOnSuccess,
  });

  useEffect(() => {
    if (!isLoadingActivitiesList) {
      if (
        issuerActivitiesList &&
        issuerActivitiesList.pages &&
        issuerActivitiesList.pages.some(page => page.data && page.data.length > 0)
      ) {
        const currentTransactions: TransactionRequest[] = [];
        // const currentConfirmationDate = format(new Date(), "yyyy-MM-dd'T'HH:mm:ssXXX");

        issuerActivitiesList.pages.forEach(page => {
          page.data?.forEach(data => {
            let quantity = 0;
            if (data.total && data.current_price_per_share && data.current_price_per_share > 0) {
              let num = new Big(data.total).div(data.current_price_per_share);

              if (allows_fractional_shares) {
                num = num.round(2, BigRoundMode.roundHalfUp);
              } else {
                num =
                  rounding_type === RoundingType.Up
                    ? num.round(0, BigRoundMode.roundUp)
                    : num.round(0, BigRoundMode.roundDown);
              }

              quantity = Number(num.valueOf());
            }

            currentTransactions.push({
              id: data.id,
              issue_date: undefined,
              memo: '',
              price_per_share: data.current_price_per_share,
              register_class_id: data.register_class_id,
              quantity: quantity,
              total: data.total,
              changed: false,
              status: data.status,
            });
          });
        });

        setDefaultPricePerShare(currentTransactions?.[0]?.price_per_share ?? 0);
        setTransactions(currentTransactions);
      } else {
        updateDividendFinalize({
          register_id: registerId,
          id: dividendId,
          shareholder_email_settings: shareholderEmailSettings,
        });
      }
    }
  }, [
    allows_fractional_shares,
    issuerActivitiesList,
    isLoadingActivitiesList,
    rounding_type,
    updateDividendFinalize,
    registerId,
    dividendId,
    shareholderEmailSettings,
  ]);

  const { isAllShareholdersSelected, isMasterCheckboxIndeterminate, handleToggleAllShareholders } =
    useShareholderEmailSelection({
      issuerActivitiesList,
      shareholderIdByEntityId,
      emailSelectionByShareholderId,
      setEmailSelectionByShareholderId,
    });

  const headItemsReinvestments = useMemo<TableHeadColumn<TypeHeadConfirmReinvestments>[]>(() => {
    return [
      { title: 'Investor', className: 'min-w-[150px] pr-2' },
      { title: 'Distributed', className: 'px-2' },
      { title: 'After rounding', className: 'px-2' },
      {
        title: 'Unit price',
        className: 'px-2',
      },
      {
        title: 'Units',
        className: 'px-2',
      },
      {
        title: 'Notify',
        className: 'w-[72px] min-w-[72px] px-0 pl-0 pr-0 text-center whitespace-nowrap',
      },
    ];
  }, []);

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!issuerActivitiesList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    issuerActivitiesList.pages.forEach(page => {
      result = result.concat(
        page.data?.map(data => {
          const transaction = transactions.find(item => item.id === data.id);
          const newTotal = Number(
            new Big(transaction?.quantity || 0)
              .mul(transaction?.price_per_share || 0)
              .round(2, BigRoundMode.roundHalfUp)
              .valueOf(),
          );
          const investorName = data.from_entity_name ?? data.from_user_name ?? '-';
          const shareholderId = getShareholderIdFromDistributionActivity(
            data,
            shareholderIdByEntityId,
          );
          const isShareholderSelected = getShareholderEmailSelectionFromActivity(
            data,
            shareholderIdByEntityId,
            emailSelectionByShareholderId,
          );
          const body: BodyCell[] = [
            {
              className: 'pr-2',
              cell: (
                <div className="flex flex-col">
                  <div className="text-sm whitespace-nowrap max-w-[190px] overflow-hidden text-ellipsis">
                    {investorName}
                  </div>
                  {/* <span className="text-xs leading-5 text-[#525252]">#{data.payment_id}</span> */}
                </div>
              ),
            },
            {
              className: 'px-2',
              cell: <div className="">{goodFormatUSDCurrency(data.total ?? 0)}</div>,
            },
            {
              className: 'px-2',
              cell: <div className="">{goodFormatUSDCurrency(newTotal ?? 0)}</div>,
            },
            {
              className: 'px-2',
              cell: (
                <div className="">
                  <CurrencyInput
                    className="min-w-0 max-w-[110px]"
                    value={transaction?.price_per_share ?? ''}
                    decimalPoints={4}
                    disabled={transaction?.status !== 'pending'}
                    error={transaction?.error}
                    classNameCurrency={classNames({
                      'bottom-[13px]': !transaction?.error,
                      'bottom-[58px]':
                        !!transaction?.error && transaction?.quantity && +transaction?.quantity > 0,
                      'bottom-[38px]':
                        (!transaction?.quantity || +transaction?.quantity === 0) &&
                        !!transaction?.error,
                    })}
                    onChange={value => {
                      if (!isNaN(+value) && isNumber(+value)) {
                        setTransactions(prev =>
                          prev.map(item => {
                            if (item.id !== data.id) {
                              return item;
                            }

                            let quantity = 0;
                            if (data.total && value && +value > 0) {
                              let num = new Big(data.total).div(+value);

                              if (allows_fractional_shares) {
                                num = num.round(2, BigRoundMode.roundHalfUp);
                              } else {
                                num =
                                  rounding_type === RoundingType.Up
                                    ? num.round(0, BigRoundMode.roundUp)
                                    : num.round(0, BigRoundMode.roundDown);
                              }

                              quantity = Number(num.valueOf());
                            }

                            return {
                              ...item,
                              price_per_share: value,
                              quantity: quantity,
                              changed: true,
                              error: '',
                            };
                          }),
                        );
                      }
                    }}
                  />
                </div>
              ),
            },
            {
              className: 'px-2',
              cell: <div className="">{goodFormatNumber(transaction?.quantity ?? 0)}</div>,
            },
            {
              className: 'w-[40px] min-w-[40px] px-0 pl-0 pr-0 text-center',
              cell: (
                <ShareholderEmailRowCheckbox
                  investorName={investorName}
                  checked={isShareholderSelected}
                  onChange={checked => {
                    if (!shareholderId) {
                      return;
                    }

                    setEmailSelectionByShareholderId(prev =>
                      updateShareholderEmailSelection(prev, shareholderId, checked),
                    );
                  }}
                />
              ),
            },
          ];

          return {
            cells: body,
          };
        }),
      );
    });

    return result;
  }, [
    allows_fractional_shares,
    emailSelectionByShareholderId,
    issuerActivitiesList,
    rounding_type,
    setEmailSelectionByShareholderId,
    shareholderIdByEntityId,
    transactions,
  ]);

  const emailPrivacyWarning = useMemo(
    () =>
      getDistributionEmailPrivacyWarning({
        issuerActivitiesList,
        shareholderIdByEntityId,
        emailSelectionByShareholderId,
      }),
    [emailSelectionByShareholderId, issuerActivitiesList, shareholderIdByEntityId],
  );

  const handleCompletePayments = useCallback(() => {
    let hasError = false;

    if (+defaultPricePerShare < 0.011) {
      setErrorDefaultPricePerShare('Price must be greater than or equal to 0.011');
      return;
    }

    const validated = transactions.map(item => {
      if (+item.price_per_share < 0.011) {
        hasError = true;
        return {
          ...item,
          error: 'Price must be greater than or equal to 0.011',
        };
      }
      return { ...item, error: '' };
    });

    setTransactions(validated);

    if (hasError) {
      return;
    }

    mutateAsync({
      transactions: transactions
        .filter(item => item.status === TransactionStatus.Pending)
        .map<TransactionSetAwaitPaymentStatusRequest>(item => ({
          quantity: item.quantity,
          price_per_share: +item.price_per_share,
          register_class_id: item.register_class_id,
          memo: item.memo,
          issue_date: item.issue_date,
          id: item.id,
        })),
    });
  }, [defaultPricePerShare, mutateAsync, transactions]);

  return (
    <div className="w-full flex flex-col gap-8">
      {/* {transactions?.length > 1 && ( */}
      <div className="flex flex-col w-full px-3 sm:px-6 gap-2">
        <CurrencyInput
          label="Default Price per Unit / NAV"
          className=""
          error={errorDefaultPricePerShare}
          value={defaultPricePerShare ?? ''}
          decimalPoints={4}
          onChange={value => {
            setErrorDefaultPricePerShare('');
            if (!isNaN(+value) && isNumber(+value)) {
              setTransactions(prev =>
                prev.map(item => {
                  if (item.changed && item.status === 'pending') {
                    return item;
                  }

                  let quantity = 0;
                  if (item.total && value && +value > 0) {
                    let num = new Big(item.total).div(+value);

                    if (allows_fractional_shares) {
                      num = num.round(2, BigRoundMode.roundHalfUp);
                    } else {
                      num =
                        rounding_type === RoundingType.Up
                          ? num.round(0, BigRoundMode.roundUp)
                          : num.round(0, BigRoundMode.roundDown);
                    }

                    quantity = Number(num.valueOf());
                  }

                  return {
                    ...item,
                    price_per_share: +value,
                    quantity: quantity,
                  };
                }),
              );
            } else {
              setTransactions(prev =>
                prev.map(item => {
                  if (item.changed && item.status === 'pending') {
                    return item;
                  }

                  return {
                    ...item,
                    price_per_share: 0,
                    quantity: 0,
                  };
                }),
              );
            }
            setDefaultPricePerShare(value);
          }}
        />
        <span className="text-xs leading-5 text-[#525252]">
          Changing the default price updates all DRPs in this distribution. To change NAV globally,
          use the checkbox below.
        </span>
        <div className="flex items-center justify-between gap-4 mt-2">
          <CustomCheckbox
            label={
              <span className="text-gray-700 text-sm font-medium">
                Set current NAV to{' '}
                <span className="font-bold text-blue-700">
                  {goodFormatUSDCurrency(defaultPricePerShare ? +defaultPricePerShare : 0)}
                </span>
              </span>
            }
            checked={isSetCurrentNAV}
            onChange={e => setIsSetCurrentNAV(e.target.checked)}
            className="select-none"
          />
          <div className="flex items-center gap-2 whitespace-nowrap shrink-0 pr-6">
            <span className="text-sm leading-5 text-gray-900">Notify all investors by email</span>
            <CustomCheckbox
              checked={isAllShareholdersSelected}
              indeterminate={isMasterCheckboxIndeterminate}
              onChange={event => handleToggleAllShareholders(event.target.checked)}
              className="select-none mr-0 leading-none"
            />
          </div>
        </div>
      </div>
      {/* // )} */}
      {isLoadingActivitiesList && (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      {!isLoadingActivitiesList && !!bodyItems.length && (
        <Table
          className="my-0"
          withRounded={false}
          withBorderX={false}
          headItems={headItemsReinvestments}
          items={bodyItems}
          isLoading={isLoadingActivitiesList}
          isError={isError}
          hasNextPageInfinite={hasNextPage}
          fetchNextPageInfinite={fetchNextPage}
          blockIdInfinite="scrollable"
          classNameHead="border-t-none"
        />
      )}
      <div className="flex flex-col w-full gap-4 px-3 sm:px-6">
        {emailPrivacyWarning && (
          <Alert icon={AlertIcon} variant="error" className="mb-0">
            <div className="whitespace-normal">{emailPrivacyWarning}</div>
          </Alert>
        )}
        <Button
          loading={isLoading || isLoadingActivitiesList || isLoadingFinalize || isLoadingAddNav}
          onClick={handleCompletePayments}
          disabled={isLoading || isLoadingActivitiesList || isLoadingFinalize || isLoadingAddNav}
          type="submit"
        >
          Confirm all reinvestments and finalise
        </Button>
        <span className="text-xs leading-5 text-[#525252]">
          By submitting this form, you move all DRPs to completed status and shares are considered
          issued at{' '}
          {format(dividendIssueDate ? new Date(dividendIssueDate) : new Date(), 'dd-MM-yyyy')}.
        </span>
      </div>
    </div>
  );
};
